import { stringifyLosslessJson } from '@pillar-portal/api-types';
import { useState, type ReactElement } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { SecretModal } from '@/components/secrets/SecretModal';
import { SecretActionButtons } from '@/components/secrets/SecretActionButtons';
import { CredentialCardView } from '@/components/secrets/CredentialCardView';
import { CredentialDeleteDialog } from '@/components/secrets/CredentialDeleteDialog';
import { ApiError, secretsApi } from '@/lib/pillarApi';
import {
    useCredentialUsages,
    useDeleteSecret,
    useDisableSecret,
    useDisconnectCredentialUsages,
    useEnableSecret,
} from '@/lib/hooks/useSecrets';
import {
    buildCredentialUsageSelectionKey,
    toCredentialUsageReference,
} from '@/lib/secrets/credentialUsageSelection';
import { parseCredentialDeleteConflictUsages } from '@/lib/secrets/credentialDeleteConflict';
import { runInBackground } from '@/lib/utils';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import type {
    CreateSecretResponse,
    CredentialType,
    CredentialUsageListItem,
    CredentialUsageReference,
    SecretIdentityReference,
    SecretListItem,
    SupportedCredentialSystemId,
} from '@pillar-portal/api-types';
import { CredentialTypeDisplayName, ThirdPartySystemIdToName } from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';
import type { CredentialTargetFamily } from '@/components/secrets/SecretsCard';

const ATTACHED_CREDENTIAL_NOT_VISIBLE_MESSAGE: string =
    'Attached credentials could not be shown. They may be unavailable in this organization ' +
    'and credential type scope, or your current role may not have permission to view them.';

function safeJsonStringifyForFilter(value: unknown): string {
    try {
        return stringifyLosslessJson(value);
    } catch {
        return '';
    }
}

export function SecretAssociationCard(props: {
    cardTitle: string;
    entityLabelForDialog: string;
    organizationUuid: string;
    organizationName: string;
    requiredCredentialType: CredentialType;
    requiredCredentialSystemId: SupportedCredentialSystemId;
    attachedSecretIdentity: Pick<SecretListItem, 'credential_uuid'> | null;
    canEdit: boolean;
    credentialTargetFamily?: CredentialTargetFamily;
    credentialSystemSelectLabel?: string;
    availableCredentialSystemIds?: readonly SupportedCredentialSystemId[];
    onAttachSecret: (secret: Pick<SecretListItem, 'credential_uuid'>) => void;
    onDetachSecret: () => void;
    onAssociationChanged?: () => Promise<void> | void;
    isMutating: boolean;
}): ReactElement {
    const queryClient = useQueryClient();
    const [isSelectingSecret, setIsSelectingSecret] = useState<boolean>(false);
    const [isSecretModalOpen, setIsSecretModalOpen] = useState<boolean>(false);
    const [secretModalMode, setSecretModalMode] = useState<'view' | 'edit' | 'create'>('create');
    const [selectedSecret, setSelectedSecret] = useState<SecretListItem | undefined>(undefined);
    const [isDisconnectConfirmationOpen, setIsDisconnectConfirmationOpen] =
        useState<boolean>(false);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
    const [attachErrorMessage, setAttachErrorMessage] = useState<string | null>(null);
    const [secretToDelete, setSecretToDelete] = useState<SecretListItem | null>(null);
    const [selectedDeleteUsageKeys, setSelectedDeleteUsageKeys] = useState<string[]>([]);
    const [deleteConflictUsages, setDeleteConflictUsages] = useState<
        CredentialUsageListItem[] | null
    >(null);
    const hasUnsavedChanges: boolean = isSecretModalOpen && secretModalMode !== 'view';

    useUnsavedChangesRegistration(hasUnsavedChanges);

    const availableSecretsQueryKey: ReadonlyArray<string | number> = [
        'paymentsInfrastructure',
        'secretAssociation',
        props.organizationUuid,
        props.requiredCredentialType,
        props.requiredCredentialSystemId,
    ];
    const availableSecretsQuery = useQuery<ReadonlyArray<SecretListItem>>({
        queryKey: availableSecretsQueryKey,
        queryFn: async () => {
            const response = await secretsApi.listSecrets(
                { organization_uuid: props.organizationUuid },
                {
                    actions: undefined,
                    credential_type: props.requiredCredentialType,
                    system_ids: [props.requiredCredentialSystemId],
                    credential_uuids: [props.attachedSecretIdentity!.credential_uuid],
                    include_inherited: true,
                    limit: 50,
                }
            );
            return response.secrets;
        },
        enabled: props.attachedSecretIdentity !== null,
    });

    const attachedSecretIdentity: Pick<SecretListItem, 'credential_uuid'> | null =
        props.attachedSecretIdentity;
    const attachedSecret: SecretListItem | null = findAttachedSecretByIdentity(
        availableSecretsQuery.data,
        attachedSecretIdentity
    );
    const secretListErrorMessage: string | null =
        availableSecretsQuery.error === null
            ? null
            : formatSecretAssociationListError(availableSecretsQuery.error);
    const deleteMutation = useDeleteSecret();
    const disableMutation = useDisableSecret();
    const enableMutation = useEnableSecret();
    const disconnectMutation = useDisconnectCredentialUsages();
    const credentialForDeleteDialog: Pick<SecretIdentityReference, 'credential_uuid'> | undefined =
        isDeleteConfirmationOpen && secretToDelete !== null
            ? {
                  credential_uuid: secretToDelete.credential_uuid,
              }
            : undefined;
    const {
        data: deleteCredentialUsagesResponse,
        isLoading: isDeleteCredentialUsagesLoading,
        error: deleteCredentialUsagesError,
    } = useCredentialUsages(credentialForDeleteDialog);

    const invalidateAvailableSecretsQuery = async (): Promise<void> => {
        await queryClient.invalidateQueries({
            queryKey: availableSecretsQueryKey,
        });
    };

    const handleViewSecret = (secret: SecretListItem): void => {
        setSelectedSecret(secret);
        setSecretModalMode('view');
        setIsSecretModalOpen(true);
    };

    const handleEditSecret = (secret: SecretListItem): void => {
        setSelectedSecret(secret);
        setSecretModalMode('edit');
        setIsSecretModalOpen(true);
    };

    const handleCreateSecret = (): void => {
        setSelectedSecret(undefined);
        setSecretModalMode('create');
        setIsSecretModalOpen(true);
    };

    const handleDisableSecret = (secret: SecretListItem): void => {
        disableMutation.mutate(
            {
                secret: {
                    credential_uuid: secret.credential_uuid,
                },
                organization: {
                    organization_uuid: props.organizationUuid,
                },
            },
            {
                onSuccess: () => {
                    runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                },
            }
        );
    };

    const handleEnableSecret = (secret: SecretListItem): void => {
        enableMutation.mutate(
            {
                secret: {
                    credential_uuid: secret.credential_uuid,
                },
                organization: {
                    organization_uuid: props.organizationUuid,
                },
            },
            {
                onSuccess: () => {
                    runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                },
            }
        );
    };

    const handleDeleteClick = (secret: SecretListItem): void => {
        setSecretToDelete(secret);
        setDeleteConflictUsages(null);
        setSelectedDeleteUsageKeys([]);
        setIsDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = (): void => {
        if (secretToDelete === null) {
            return;
        }
        deleteMutation.mutate(
            {
                secret: {
                    credential_uuid: secretToDelete.credential_uuid,
                },
                organization: {
                    organization_uuid: props.organizationUuid,
                },
            },
            {
                onSuccess: () => {
                    setIsDeleteConfirmationOpen(false);
                    setSecretToDelete(null);
                    setDeleteConflictUsages(null);
                    setSelectedDeleteUsageKeys([]);
                    runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                },
                onError: (deleteError: unknown) => {
                    const conflictUsages: CredentialUsageListItem[] | null =
                        parseCredentialDeleteConflictUsages(deleteError);
                    if (conflictUsages !== null) {
                        setDeleteConflictUsages(conflictUsages);
                    }
                },
            }
        );
    };

    const deleteCredentialUsages: CredentialUsageListItem[] =
        deleteConflictUsages ?? deleteCredentialUsagesResponse?.usages ?? [];
    const getSystemName = (systemId: SecretListItem['system_id']): string => {
        const systemName = ThirdPartySystemIdToName[systemId];
        if (systemName !== undefined) {
            return systemName
                .toLowerCase()
                .split('_')
                .map((segment) => {
                    if (segment.length <= 2) {
                        return segment.toUpperCase();
                    }
                    return segment.charAt(0).toUpperCase() + segment.slice(1);
                })
                .join(' ');
        }
        return `System ${systemId}`;
    };

    const handleToggleDeleteUsageSelection = (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ): void => {
        const usageSelectionKey: string = buildCredentialUsageSelectionKey(usage);
        if (checked) {
            setSelectedDeleteUsageKeys((currentSelection) =>
                currentSelection.includes(usageSelectionKey)
                    ? currentSelection
                    : [...currentSelection, usageSelectionKey]
            );
            return;
        }
        setSelectedDeleteUsageKeys((currentSelection) =>
            currentSelection.filter((currentKey) => currentKey !== usageSelectionKey)
        );
    };

    const handleToggleDeleteSelectAll = (checked: boolean): void => {
        if (!checked) {
            setSelectedDeleteUsageKeys([]);
            return;
        }
        setSelectedDeleteUsageKeys(
            deleteCredentialUsages.map((usage) => buildCredentialUsageSelectionKey(usage))
        );
    };

    const handleRemoveDeleteUsages = (): void => {
        if (secretToDelete === null) {
            return;
        }
        const selectedUsages: CredentialUsageReference[] = deleteCredentialUsages
            .filter((usage) =>
                selectedDeleteUsageKeys.includes(buildCredentialUsageSelectionKey(usage))
            )
            .map(toCredentialUsageReference);
        if (selectedUsages.length === 0) {
            return;
        }
        disconnectMutation.mutate(
            {
                secret: {
                    credential_uuid: secretToDelete.credential_uuid,
                },
                organization: {
                    organization_uuid: props.organizationUuid,
                },
                usages: selectedUsages,
            },
            {
                onSuccess: () => {
                    setSelectedDeleteUsageKeys([]);
                    setDeleteConflictUsages(null);
                    runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                    if (props.onAssociationChanged !== undefined) {
                        runInBackground(
                            Promise.resolve(props.onAssociationChanged()),
                            () => undefined
                        );
                    }
                },
            }
        );
    };

    return (
        <>
            <Card
                data-testid={`${props.entityLabelForDialog.replaceAll(' ', '-')}-credentials-card`}
            >
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>{props.cardTitle}</CardTitle>
                    {props.canEdit && attachedSecretIdentity === null && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                    setIsSelectingSecret((currentValue: boolean) => !currentValue)
                                }
                            >
                                Select Credentials
                            </Button>
                            <Button
                                id={`${props.entityLabelForDialog.replaceAll(' ', '-')}-create-credentials`}
                                size="sm"
                                onClick={handleCreateSecret}
                            >
                                Create Credentials
                            </Button>
                        </div>
                    )}
                    {props.canEdit && attachedSecretIdentity !== null && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setIsDisconnectConfirmationOpen(true)}
                            disabled={props.isMutating}
                        >
                            Reset Credentials
                        </Button>
                    )}
                </CardHeader>
                <CardContent className="space-y-4">
                    {deleteMutation.error !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {deleteMutation.error instanceof Error
                                    ? deleteMutation.error.message
                                    : 'Failed to delete credential'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {disableMutation.error !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {disableMutation.error instanceof Error
                                    ? disableMutation.error.message
                                    : 'Failed to disable credential'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {enableMutation.error !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {enableMutation.error instanceof Error
                                    ? enableMutation.error.message
                                    : 'Failed to enable credential'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {disconnectMutation.error !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {disconnectMutation.error instanceof Error
                                    ? disconnectMutation.error.message
                                    : 'Failed to disconnect credential usages'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {attachedSecretIdentity === null ? (
                        <p className="text-muted-foreground">
                            No secret is currently attached to this {props.entityLabelForDialog}.
                        </p>
                    ) : (
                        <>
                            {availableSecretsQuery.isLoading && (
                                <p className="text-muted-foreground">Loading secret details...</p>
                            )}
                            {secretListErrorMessage !== null && (
                                <Alert variant="destructive">
                                    <AlertDescription>{secretListErrorMessage}</AlertDescription>
                                </Alert>
                            )}
                            {attachedSecret !== null && (
                                <CredentialCardView
                                    id={`${props.entityLabelForDialog.replaceAll(' ', '-')}-attached-credential`}
                                    credential={attachedSecret}
                                    showPublicFields={true}
                                    actions={
                                        <SecretActionButtons
                                            secret={attachedSecret}
                                            onView={() => handleViewSecret(attachedSecret)}
                                            onEdit={() => handleEditSecret(attachedSecret)}
                                            onDisable={() => handleDisableSecret(attachedSecret)}
                                            onEnable={() => handleEnableSecret(attachedSecret)}
                                            onDelete={() => handleDeleteClick(attachedSecret)}
                                            onDisconnect={() =>
                                                setIsDisconnectConfirmationOpen(true)
                                            }
                                            disconnectTitle={`Disconnect this credential from this ${props.entityLabelForDialog}`}
                                        />
                                    }
                                />
                            )}
                            {attachedSecret === null &&
                                availableSecretsQuery.isLoading === false &&
                                availableSecretsQuery.error === null && (
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            {ATTACHED_CREDENTIAL_NOT_VISIBLE_MESSAGE}
                                        </AlertDescription>
                                    </Alert>
                                )}
                        </>
                    )}

                    {isSelectingSecret && props.canEdit && attachedSecretIdentity === null && (
                        <OmnisearchSelect<SecretListItem, string>
                            label="Select Credentials"
                            placeholder="Search secrets"
                            pageSize={50}
                            fetchOnEmptyQuery={true}
                            selectedItemId={null}
                            fetchPage={async ({ terms, limit, cursor }) => {
                                const response = await secretsApi.listSecrets(
                                    { organization_uuid: props.organizationUuid },
                                    {
                                        credential_type: props.requiredCredentialType,
                                        system_ids: [props.requiredCredentialSystemId],
                                        include_inherited: true,
                                        terms: terms === undefined ? undefined : [...terms],
                                        limit,
                                        cursor,
                                    }
                                );
                                if (response.pagination === undefined) {
                                    throw new Error(
                                        'Paginated credential search omitted pagination'
                                    );
                                }
                                return {
                                    items: filterAvailableSecretsForAssociation(response.secrets),
                                    pagination: response.pagination,
                                };
                            }}
                            getItemId={(secret) => secret.credential_uuid}
                            getItemLabel={(secret) =>
                                `${secret.account_id} (${CredentialTypeDisplayName[secret.credential_type]})`
                            }
                            getItemFilterText={(secret) =>
                                `${secret.account_id} ${secret.credential_uuid} ${safeJsonStringifyForFilter(secret.public_fields)}`.toLowerCase()
                            }
                            onSelect={(secret) => {
                                setAttachErrorMessage(null);
                                props.onAttachSecret(toSecretAssociationIdentity(secret));
                                setIsSelectingSecret(false);
                            }}
                            disabled={props.isMutating}
                        />
                    )}

                    {attachErrorMessage !== null && (
                        <p className="text-destructive">{attachErrorMessage}</p>
                    )}

                    {props.isMutating && (
                        <p className="text-muted-foreground">
                            Updating secret association for this {props.entityLabelForDialog}...
                        </p>
                    )}
                </CardContent>
            </Card>

            <SecretModal
                open={isSecretModalOpen}
                onOpenChange={(open) => {
                    setIsSecretModalOpen(open);
                    if (!open) {
                        setSelectedSecret(undefined);
                        runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                    }
                }}
                organizationName={props.organizationName}
                organizationUuid={props.organizationUuid}
                secret={selectedSecret}
                mode={secretModalMode}
                lockedCredentialType={
                    secretModalMode === 'create' ? props.requiredCredentialType : undefined
                }
                credentialTargetFamily={props.credentialTargetFamily}
                credentialSystemSelectLabel={props.credentialSystemSelectLabel}
                availableCredentialSystemIds={props.availableCredentialSystemIds}
                initialCredentialSystemId={props.requiredCredentialSystemId}
                onSecretSaved={(savedSecret: CreateSecretResponse) => {
                    setAttachErrorMessage(null);
                    props.onAttachSecret(toSecretAssociationIdentity(savedSecret));
                    runInBackground(invalidateAvailableSecretsQuery(), (error: unknown) => {
                        setAttachErrorMessage(
                            error instanceof Error
                                ? error.message
                                : 'Unable to refresh the newly created secret.'
                        );
                    });
                }}
            />

            <ConfirmationModal
                open={isDisconnectConfirmationOpen}
                onOpenChange={setIsDisconnectConfirmationOpen}
                title="Disconnect Credential"
                message={`Disconnect this credential from this ${props.entityLabelForDialog}? The credential will not be deleted.`}
                onConfirm={() => {
                    props.onDetachSecret();
                    setIsDisconnectConfirmationOpen(false);
                }}
                confirmLabel="Disconnect"
                cancelLabel="Cancel"
                isLoading={props.isMutating}
            />

            <CredentialDeleteDialog
                open={isDeleteConfirmationOpen}
                onOpenChange={(open) => {
                    setIsDeleteConfirmationOpen(open);
                    if (!open) {
                        setSecretToDelete(null);
                        setDeleteConflictUsages(null);
                        setSelectedDeleteUsageKeys([]);
                    }
                }}
                credentialLabel={
                    secretToDelete === null
                        ? ''
                        : `${getSystemName(secretToDelete.system_id)} (${secretToDelete.account_id})`
                }
                usages={deleteCredentialUsages}
                isLoadingUsages={isDeleteCredentialUsagesLoading}
                usageLoadError={deleteCredentialUsagesError}
                selectedUsageKeys={selectedDeleteUsageKeys}
                onToggleUsageSelection={handleToggleDeleteUsageSelection}
                onToggleSelectAll={handleToggleDeleteSelectAll}
                onConfirm={handleConfirmDelete}
                onRemove={handleRemoveDeleteUsages}
                isDeleting={deleteMutation.isPending}
                isRemoving={disconnectMutation.isPending}
            />
        </>
    );
}

export function toSecretAssociationIdentity(
    secret: Pick<SecretListItem, 'credential_uuid'>
): Pick<SecretListItem, 'credential_uuid'> {
    return {
        credential_uuid: secret.credential_uuid,
    };
}

export function findAttachedSecretByIdentity(
    availableSecrets: ReadonlyArray<SecretListItem> | undefined,
    attachedSecretIdentity: Pick<SecretListItem, 'credential_uuid'> | null
): SecretListItem | null {
    if (availableSecrets === undefined || attachedSecretIdentity === null) {
        return null;
    }
    return (
        availableSecrets.find(
            (secret: SecretListItem) =>
                secret.credential_uuid === attachedSecretIdentity.credential_uuid
        ) ?? null
    );
}

export function filterAvailableSecretsForAssociation(
    secrets: ReadonlyArray<SecretListItem>
): ReadonlyArray<SecretListItem> {
    return secrets.filter(
        (secret: SecretListItem) =>
            secret.status === Status.ACTIVE || secret.status === Status.PENDING
    );
}

function formatSecretAssociationListError(error: unknown): string {
    if (error instanceof ApiError) {
        return error.message;
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'Failed to load attached credential details.';
}
